import { db, dbHelpers } from './db';
import { authFetch } from '../utils/apiClient';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000]; // Exponential backoff
const MAX_RETRIES = 5;

class SyncQueue {
  constructor() {
    this.isProcessing = false;
    this.isOnline = navigator.onLine;
    this.setupListeners();
  }

  setupListeners() {
    // Network status listeners
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processPendingItems();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Periodic check (every 30 seconds)
    setInterval(() => {
      if (this.isOnline && !this.isProcessing) {
        this.processPendingItems();
      }
    }, 30000);
  }

  async addItem(action, resourceType, payload) {
    const item = await dbHelpers.addToSyncQueue(action, resourceType, payload);
    
    // Try to process immediately if online
    if (this.isOnline) {
      setTimeout(() => this.processPendingItems(), 100);
    }
    
    return item;
  }

  async processPendingItems() {
    if (this.isProcessing || !this.isOnline) {
      return;
    }

    this.isProcessing = true;

    try {
      const pendingItems = await dbHelpers.getPendingSyncItems();

      for (const item of pendingItems) {
        try {
          await this.processItem(item);
          
          // Mark as completed
          await dbHelpers.updateSyncItem(item.id, {
            status: 'completed',
            lastError: null
          });
        } catch (error) {
          const retryCount = item.retryCount + 1;
          
          if (retryCount >= MAX_RETRIES) {
            // Max retries reached, mark as failed
            await dbHelpers.updateSyncItem(item.id, {
              status: 'failed',
              retryCount,
              lastError: error.message
            });
          } else {
            // Schedule retry
            await dbHelpers.updateSyncItem(item.id, {
              retryCount,
              lastError: error.message
            });
          }
        }
      }

      // Cleanup old completed items
      await dbHelpers.clearCompletedSyncItems();
    } finally {
      this.isProcessing = false;
    }
  }

  async processItem(item) {
    const { action, resourceType, payload } = item;
    const endpoint = this.getEndpoint(resourceType, action);

    let method = 'POST';
    if (action === 'update') method = 'PUT';
    if (action === 'delete') method = 'DELETE';

    const response = await authFetch(`${API_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return await response.json();
  }

  getEndpoint(resourceType, action) {
    switch (resourceType) {
      case 'product':
        if (action === 'create') return '/api/inventory/products';
        if (action === 'update') return `/api/inventory/products/${action.id}`;
        if (action === 'delete') return `/api/inventory/products/${action.id}`;
        break;
      case 'inventory':
        return '/api/inventory/update';
      case 'transaction':
        return '/api/inventory/transactions';
      default:
        throw new Error(`Unknown resource type: ${resourceType}`);
    }
  }

  async getSyncStatus() {
    const pending = await db.syncQueue.where('status').equals('pending').count();
    const failed = await db.syncQueue.where('status').equals('failed').count();
    
    return {
      isOnline: this.isOnline,
      isProcessing: this.isProcessing,
      pendingCount: pending,
      failedCount: failed,
      lastCheck: new Date().toISOString()
    };
  }

  async retryFailed() {
    // Reset failed items to pending
    const failedItems = await db.syncQueue
      .where('status')
      .equals('failed')
      .toArray();

    for (const item of failedItems) {
      await dbHelpers.updateSyncItem(item.id, {
        status: 'pending',
        retryCount: 0,
        lastError: null
      });
    }

    console.log(`🔄 Reset ${failedItems.length} failed items to pending`);
    
    if (this.isOnline) {
      this.processPendingItems();
    }
  }
}

// Create singleton instance
export const syncQueue = new SyncQueue();

// Export helper functions
export const queueProduct = (action, product) => 
  syncQueue.addItem(action, 'product', product);

export const queueInventoryUpdate = (update) => 
  syncQueue.addItem('update', 'inventory', update);

export const queueTransaction = (transaction) => 
  syncQueue.addItem('create', 'transaction', transaction);

export default syncQueue;
